import { useCallback } from 'react';
import { Recipe } from '../../api/Recipe';
import { apiModules } from '../../api/apiService';
import { uniqueFavoriteRecipes } from '../../domains/favorites/cache';
import { executeOptimisticFavoriteMutation } from '../../domains/favorites/mutations';

interface UseFavoriteMutationsOptions {
  authorId?: number;
  favoritesRef: React.RefObject<Recipe[]>;
  setCachedFavorites: (favorites: Recipe[]) => void;
  setFavoritePendingIds: React.Dispatch<React.SetStateAction<string[]>>;
}

interface UseFavoriteMutationsResult {
  addFavorite: (recipe: Recipe) => Promise<void>;
  removeFavorite: (recipeId: string) => Promise<void>;
}

const hasFavoriteRecipe = (favorites: Recipe[], recipeId: string) => (
  favorites.some((favorite) => favorite.id === recipeId)
);

const requireAuthenticatedAuthor = (authorId: number | undefined, action: 'ajouter' | 'retirer') => {
  if (!authorId) {
    throw new Error(`Vous devez etre connecte pour ${action} un favori.`);
  }

  return authorId;
};

const useFavoriteMutations = ({
  authorId,
  favoritesRef,
  setCachedFavorites,
  setFavoritePendingIds,
}: UseFavoriteMutationsOptions): UseFavoriteMutationsResult => {
  const addFavorite = useCallback(async (recipe: Recipe) => {
    const authenticatedAuthorId = requireAuthenticatedAuthor(authorId, 'ajouter');
    const currentFavorites = favoritesRef.current;

    if (hasFavoriteRecipe(currentFavorites, recipe.id)) {
      return;
    }

    const previousFavorites = currentFavorites;
    const nextFavorites = uniqueFavoriteRecipes([recipe, ...previousFavorites]);

    await executeOptimisticFavoriteMutation({
      recipeId: recipe.id,
      nextFavorites,
      previousFavorites,
      setCachedFavorites,
      setFavoritePendingIds,
      mutation: () => apiModules.recipes.addFavorite(recipe.id, authenticatedAuthorId),
    });
  }, [authorId, favoritesRef, setCachedFavorites, setFavoritePendingIds]);

  const removeFavorite = useCallback(async (recipeId: string) => {
    const authenticatedAuthorId = requireAuthenticatedAuthor(authorId, 'retirer');
    const currentFavorites = favoritesRef.current;

    if (!hasFavoriteRecipe(currentFavorites, recipeId)) {
      return;
    }

    const previousFavorites = currentFavorites;
    const nextFavorites = previousFavorites.filter((favorite) => favorite.id !== recipeId);

    await executeOptimisticFavoriteMutation({
      recipeId,
      nextFavorites,
      previousFavorites,
      setCachedFavorites,
      setFavoritePendingIds,
      mutation: () => apiModules.recipes.removeFavorite(recipeId, authenticatedAuthorId),
    });
  }, [authorId, favoritesRef, setCachedFavorites, setFavoritePendingIds]);

  return {
    addFavorite,
    removeFavorite,
  };
};

export default useFavoriteMutations;
